import {Directive, ElementRef, HostListener, Renderer2, forwardRef, Input} from '@angular/core';
import {NG_VALUE_ACCESSOR, ControlValueAccessor} from '@angular/forms';
import {TranslocoService} from "@ngneat/transloco";

const NUMBER_FORMATTER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => NumberFormatterDirective),
  multi: true
};

@Directive({
  selector: '[numberFormatter]',
  providers: [NUMBER_FORMATTER_VALUE_ACCESSOR]

})
export class NumberFormatterDirective implements ControlValueAccessor {

  @Input() decimalPlaces: number = 2;

  private onChange: any = () => {
  };
  private onTouched: any = () => {
  };

  private DECIMAL_SEPARATOR: string;
  private THOUSANDS_SEPARATOR: string;

  private el: HTMLInputElement;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private transloco: TranslocoService
) {
    this.el = this.elementRef.nativeElement;
    this.DECIMAL_SEPARATOR = this.transloco.getActiveLang() === "en" ? "." : ",";
    this.THOUSANDS_SEPARATOR = this.transloco.getActiveLang() === "en" ? "," : ".";
  }

  @HostListener('input', ['$event']) onInput(): void {
    let inputValue: string = this.el.value.replace(new RegExp(`[^0-9${this.DECIMAL_SEPARATOR}]`, 'g'), '');
    inputValue = this.formatInput(inputValue);
    this.setOnChange(inputValue)
    this.renderer.setProperty(this.el, 'value', inputValue);
  }

  @HostListener('blur', ['$event']) onBlur(): void {
    let inputValue: string = this.el.value;
    inputValue = this.roundToDecimal(inputValue);
    this.setOnChange(inputValue)
    this.renderer.setProperty(this.el, 'value', inputValue);
    this.onTouched();
  }

  @HostListener('focus', ['$event']) onFocus(): void {
    let inputValue: string = this.el.value;
    inputValue = this.removeTrailingZeros(inputValue);
    this.setOnChange(inputValue)
    this.renderer.setProperty(this.el, 'value', inputValue);
  }

  writeValue(value: any): void {
    if (value !== undefined && value !== null) {
      this.renderer.setProperty(this.el, 'value', this.roundToDecimal(value.toString()));
    } else {
      this.renderer.setProperty(this.el, 'value', '');
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el, 'disabled', isDisabled);
  }

  private setOnChange(inputValue: string) {
    const numberValue = this.parseStringToFloat(inputValue)
    this.onChange(!isNaN(numberValue) ? numberValue : undefined);
  }

  private getStringRepresentationOfFloat(value:string): string {
    if (/^\d+(\.\d+)?$/.test(value)) {
      return value; // Return the value as it is if it's already a valid number format
    }
    value = value.replaceAll(this.THOUSANDS_SEPARATOR, "");
    value = value.replace(/([.,])\1+/g, '$1');
    const parts = value.split(this.DECIMAL_SEPARATOR)
    if (parts.length === 1) {
      value = parts[0].toString();
    } else {
      value = parts.slice(0, parts.length - 1).join('') + this.DECIMAL_SEPARATOR + parts[parts.length - 1];
    }
    return value.replaceAll(this.DECIMAL_SEPARATOR, ".");
  }

  private parseStringToFloat(value: string): number {
    return parseFloat(this.getStringRepresentationOfFloat(value));
  }

  private splitNumber(value: string) {
    const [integerPart, decimalPart] = value.split(".");
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, this.THOUSANDS_SEPARATOR);
    return [formattedInteger, decimalPart];
  }

  private formatInput(value: string): string {
    value = this.getStringRepresentationOfFloat(value);
    const numberValue: number = parseFloat(value);
    if (!isNaN(numberValue)) {
      const [integerPart, decimalPart] = this.splitNumber(value);
      return decimalPart !== undefined ? `${integerPart}${this.DECIMAL_SEPARATOR}${decimalPart}` : integerPart;
    } else {
      return '';
    }
  }


  private roundToDecimal(value: string): string {
    const numberValue: number = this.parseStringToFloat(value);

    if (!isNaN(numberValue)) {
      const [integerPart, decimalPart] = this.splitNumber(numberValue.toFixed(this.decimalPlaces))
      return `${integerPart}${this.DECIMAL_SEPARATOR}${decimalPart}`;
    } else {
      return '';
    }
  }

  private removeTrailingZeros(value: string): string {
    const [integerPart, decimalPart] = value.split(this.DECIMAL_SEPARATOR);
    if (decimalPart) {
      const formattedDecimal = decimalPart.replace(/0+$/, '');
      return formattedDecimal ? `${integerPart}${this.DECIMAL_SEPARATOR}${formattedDecimal}` : integerPart;
    } else {
      return value;
    }
  }
}
